import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { soccerPath, soccerUrl } from '../utils/paths';
import './TeamView.css';

const TeamView = () => {
  const { id } = useParams();
  const { user, isAdmin } = useAuth();
  const [team, setTeam] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Check if user can view skill levels (admin or creator)
  const canViewSkillLevel = () => {
    if (isAdmin()) return true;
    if (user?.roles && user.roles.includes('TEAM_CREATOR')) return true;
    return false;
  };

  const loadTeam = useCallback(async () => {
    try {
      setLoading(true);
      const data = await api.getTeamById(id, user?.id);
      setTeam(data);
      setError('');
    } catch (err) {
      setError(err.message || 'Failed to load team');
    } finally {
      setLoading(false);
    }
  }, [id, user?.id]);

  useEffect(() => {
    loadTeam();
  }, [loadTeam]);

  const getSkillBadgeClass = (skill) => {
    if (skill >= 8) return 'high';
    if (skill >= 5) return 'medium';
    return 'low';
  };

  const formatPosition = (position) => {
    return position.charAt(0) + position.slice(1).toLowerCase().replace('_', ' ');
  };

  // Helper function to properly encode message for WhatsApp with emoji support
  const encodeWhatsAppMessage = (message) => {
    return encodeURIComponent(message);
  };

  // Get WhatsApp URL - use api.whatsapp.com for better emoji support
  const getWhatsAppUrl = (message) => {
    const encodedMessage = encodeWhatsAppMessage(message);
    return `https://api.whatsapp.com/send?text=${encodedMessage}`;
  };

  const shareViaWhatsApp = () => {
    if (!team) return;

    // Define position order for sorting
    const positionOrder = {
      'GOALKEEPER': 1,
      'DEFENDER': 2,
      'MIDFIELDER': 3,
      'WINGER': 4,
      'FORWARD': 5,
      'FLEXIBLE': 6
    };

    // Function to group players by position, then sort alphabetically within each group
    const groupPlayersByPosition = (players) => {
      const grouped = {};
      
      // Group players by position
      players.forEach((player) => {
        const position = player.position || 'FLEXIBLE';
        if (!grouped[position]) {
          grouped[position] = [];
        }
        grouped[position].push(player);
      });
      
      // Sort players within each position group alphabetically
      Object.keys(grouped).forEach((position) => {
        grouped[position].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      });
      
      return grouped;
    };

    let message = `⚽ *${team.name}*\n\n`;
    
    if (team.description) {
      message += `${team.description}\n\n`;
    }
    
    message += `👤 Creator: ${team.creator?.name || 'N/A'}\n`;
    message += `👥 Total Players: ${team.members.length}\n`;
    message += `🏆 Teams: ${team.numberOfTeams}\n\n`;
    
    if (team.divisions && team.divisions.length > 0) {
      message += `*Team Divisions:*\n\n`;
      
      team.divisions.forEach((division, idx) => {
        message += `*${division.teamName}*`;
        if (division.averageSkill) {
          message += ` (Avg Skill: ${division.averageSkill.toFixed(1)}/10)`;
        }
        message += `\n`;
        
        if (division.players && division.players.length > 0) {
          // Group players by position and sort alphabetically within each group
          const groupedPlayers = groupPlayersByPosition(division.players);
          
          // Display players grouped by position
          Object.keys(positionOrder).forEach((positionKey) => {
            if (groupedPlayers[positionKey] && groupedPlayers[positionKey].length > 0) {
              const positionName = formatPosition(positionKey);
              message += `*${positionName}s:*\n`;
              groupedPlayers[positionKey].forEach((player) => {
                message += `• ${player.name} - ${positionName}\n`;
              });
              message += `\n`;
            }
          });
        } else {
          message += `\n`;
        }
      });
    } else {
      message += `*All Players:*\n\n`;
      // Group players by position and sort alphabetically within each group
      const groupedMembers = groupPlayersByPosition(team.members);
      
      // Display players grouped by position
      Object.keys(positionOrder).forEach((positionKey) => {
        if (groupedMembers[positionKey] && groupedMembers[positionKey].length > 0) {
          const positionName = formatPosition(positionKey);
          message += `*${positionName}s:*\n`;
          groupedMembers[positionKey].forEach((player) => {
            message += `• ${player.name} - ${positionName}\n`;
          });
          message += `\n`;
        }
      });
    }
    
    // Add link to view team online
    const teamUrl = soccerUrl(`/teams/${id}`);
    message += `View team online: ${teamUrl}`;
    
    // Use api.whatsapp.com for better emoji support
    const whatsappUrl = getWhatsAppUrl(message);
    
    // Open WhatsApp in new window
    window.open(whatsappUrl, '_blank');
  };

  if (loading) {
    return <div className="loading">Loading team details...</div>;
  }

  if (error) {
    return (
      <div>
        <div className="alert alert-error">{error}</div>
        <Link to={soccerPath('/teams')} className="btn btn-secondary">Back to Teams</Link>
      </div>
    );
  }

  if (!team) {
    return <div className="empty-state">Team not found</div>;
  }

  return (
    <div>
      <div className="team-view-actions">
        <Link to={soccerPath('/teams')} className="btn btn-secondary">← Back to Teams</Link>
        <Link to={soccerPath(`/teams/${id}/edit`)} className="btn btn-primary">Edit Team</Link>
        <button 
          onClick={shareViaWhatsApp} 
          className="btn btn-whatsapp"
        >
          <span>📱</span> Share via WhatsApp
        </button>
      </div>

      <div className="card">
        <h2>{team.name}</h2>
        {team.description && (
          <p style={{ color: '#666', marginBottom: '1rem' }}>{team.description}</p>
        )}
        
        <div className="team-view-header">
          <div>
            <strong>Creator:</strong> {team.creator.name}
          </div>
          <div>
            <strong>Total Players:</strong> {team.members.length}
          </div>
          <div>
            <strong>Number of Teams:</strong> {team.numberOfTeams}
          </div>
          <div>
            <strong>Created:</strong> {new Date(team.createdAt).toLocaleString()}
          </div>
        </div>

        <h3 style={{ marginBottom: '1rem' }}>All Team Members</h3>
        <div className="player-list" style={{ marginBottom: '2rem' }}>
          {team.members.map((player) => (
            <div key={player.id} className="player-card">
              <h4>{player.name}</h4>
              <div className="player-info">
                <strong>Position:</strong> {formatPosition(player.position)}
              </div>
              {canViewSkillLevel() && (
                <div className="player-info">
                  <strong>Skill:</strong>{' '}
                  <span className={`skill-badge ${getSkillBadgeClass(player.skillLevel)}`}>
                    {player.skillLevel}/10
                  </span>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {team.divisions && team.divisions.length > 0 && (
        <div className="card">
          <h2>Team Divisions</h2>
          <p style={{ color: '#666', marginBottom: '1.5rem' }}>
            Players have been divided into balanced teams based on skill levels
          </p>
          
          <div className="team-divisions">
            {team.divisions.map((division, idx) => (
              <div key={idx} className="division-card">
                <h4>
                  {division.teamName}
                  {division.averageSkill && (
                    <span style={{ marginLeft: '1rem', fontSize: '1rem', color: 'var(--color-brand)', fontWeight: 'normal' }}>
                      (Average Skill: {division.averageSkill.toFixed(1)}/10)
                    </span>
                  )}
                </h4>
                
                <div className="division-players">
                  {division.players.map((player) => (
                    <div key={player.id} className="player-tag">
                      <strong>{player.name}</strong>
                      <br />
                      <small>
                        {formatPosition(player.position)}
                        {canViewSkillLevel() && ` • Skill: ${player.skillLevel}/10`}
                      </small>
                    </div>
                  ))}
                </div>
                
                <div className="division-stats">
                  Total Players: {division.players.length}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamView;

